import { useMap } from "@vis.gl/react-maplibre";
import { debounce } from "lodash";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  GOOGLE_ATTRIBUTION_FALLBACK,
  GOOGLE_ATTRIBUTION_FETCH_DELAY_MS,
  GoogleApi,
  googleApiKey,
  googleSession,
  IMapViewPort,
  Log,
} from "@ryde/shared";

import { getAliveMap } from "./maplibre-map.utils";

/**
 * Google's per-viewport copyright line, re-fetched as the map moves — the MapLibre port of the Leaflet
 * `AttributionUpdater`.
 *
 * Google's Tiles terms require the copyright for the imagery actually in view, and it varies by region and
 * zoom, so a static "© Google" is not enough. The first wave of MapLibre maps shipped without this and
 * showed only the raster source's static attribution; this closes that gap before the rest of the maps
 * move over and inherit it.
 *
 * Renders nothing unless Google tiles are active — the host mounts it only in that case.
 *
 * Unlike the Leaflet version it holds the text in local state rather than the global
 * `googleMapAttributesAtom`. Two maps can be mounted at once (a grid map behind an open drawer map), and a
 * shared atom means whichever moved last overwrites the other's attribution.
 */
export const MapLibreGoogleAttribution: FC = () => {
  const { current: mapRef } = useMap();
  const sessionObj = useRecoilValue(googleSession);
  const [attribution, setAttribution] = useState(GOOGLE_ATTRIBUTION_FALLBACK);
  const sessionToken = sessionObj?.session;

  /*
   * The fetch outlives the component on a fast close — this map lives in a grid panel and in a drawer, both
   * of which unmount while a request is in flight. Writing state afterwards warns and leaks.
   */
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updateAttribution = useEvent(async (): Promise<void> => {
    const map = getAliveMap(mapRef);

    if (!map || !sessionToken) {
      return;
    }

    try {
      const bounds = map.getBounds();
      const viewport: IMapViewPort = {
        zoom: map.getZoom(),
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      };

      // GoogleApi.getViewportAttribution already catches and falls back, so this cannot reject.
      const nextAttribution = await GoogleApi.getViewportAttribution({
        viewport,
        sessionToken,
        apiKey: googleApiKey(),
      });

      if (isMountedRef.current) {
        setAttribution(nextAttribution || GOOGLE_ATTRIBUTION_FALLBACK);
      }
    } catch (error) {
      Log.warn("MapLibre viewport attribution exception", error);
    }
  });

  const debouncedUpdate = useMemo(
    () => debounce(updateAttribution, GOOGLE_ATTRIBUTION_FETCH_DELAY_MS),
    [updateAttribution],
  );

  useEffect(() => {
    const map = getAliveMap(mapRef);

    if (!map || !sessionToken) {
      return;
    }

    void updateAttribution();
    map.on("moveend", debouncedUpdate);
    map.on("zoomend", debouncedUpdate);

    return () => {
      debouncedUpdate.cancel();
      const aliveMap = getAliveMap(mapRef);
      aliveMap?.off("moveend", debouncedUpdate);
      aliveMap?.off("zoomend", debouncedUpdate);
    };
  }, [mapRef, sessionToken, debouncedUpdate, updateAttribution]);

  if (!attribution) {
    return null;
  }

  return <div className="google-maps-attribution">{attribution}</div>;
};
